/* eslint-disable react/jsx-props-no-spreading */
/**
 * @description This context provides a callback function that components can use to display a
 *  confirmation dialog before committing an action.  The provider mounts a ConfirmDialog
 *  component at the bottom of the application and the context provides the necessary function
 *  to toggle the dialog's visibility, render the appropriate title and description, define a
 *  callback function to be run on confirmation, and (optionally) a callback function to run
 *  if canceled.  The confirmation callback can return an promise such that, until the promise
 *  is resolved, the dialog box will show a loading spinner while the action is being performed.
 *
 *  Example:  const confirm = React.useContext(ConfirmDialogContext);
 *  ...
 *  function handleAction () {
 *    confirm('Confirmation', 'Are you sure you want to do that bub?', runAsyncAction())
 *  }
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import Spinner from '@seccon/shared/ui/components/Spinner';

const styles = {
  title: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between'
  }
};

const ConfirmDialog = ({
  isLoading,
  message,
  onCancel,
  onConfirm,
  open,
  options,
  title
}) => (
  <Dialog
    {...options}
    open={open}
    onClose={() => onCancel()}
    aria-labelledby='confirm-dialog-title'
    aria-describedby='confirm-dialog-description'
  >
    { /* Only render the dialog contents when openned */ }
    {open && (
      <>
        {title && (
          <DialogTitle id='confirm-dialog-title' sx={styles.title}>
            {title}
            {isLoading ? <Spinner width='2rem' height='2rem' /> : null}
          </DialogTitle>
        )}
        {typeof message === 'string' ? (
          <DialogContent dividers>
            <DialogContentText id='confirm-dialog-description'>
              {message}
            </DialogContentText>
          </DialogContent>
        ) : message}
        <DialogActions>
          <Button onClick={() => onCancel()}>Cancel</Button>
          <Button disabled={isLoading} onClick={onConfirm} autoFocus>Confirm</Button>
        </DialogActions>
      </>
    )}
  </Dialog>
);

ConfirmDialog.propTypes = {
  isLoading: PropTypes.bool,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  options: PropTypes.object,
  title: PropTypes.string
};

ConfirmDialog.defaultProps = {
  isLoading: false,
  message: null,
  options: null,
  title: null
};

export const ConfirmDialogContext = React.createContext(() => console.error('ConfirmDialogProvider is not defined'));

export const ConfirmDialogProvider = ({ children }) => {
  // Manages the dialog visibility state
  const [open, setOpen] = React.useState(false);

  // Manages the loading state of the dialog
  const [isLoading, setIsLoading] = React.useState(false);

  /*
   * Parameters are stored as references because onConfirmDialog is a memoized function. So in order to access these
   *  variables in a memoized function, the function must either (A) define title, message, onConfirm, and onCancel as
   *  dependencies or (B) save these parameters in references. Since I don't want to redefine the api function whenever
   *  these parameters change (because that would update the context value and cause unnecessary re-renders), I went with
   *  option B.
   */
  const optionsRef = React.useRef(null);
  const titleRef = React.useRef(null);
  const messageRef = React.useRef(null);
  const onConfirmRef = React.useRef(null);
  const onCancelRef = React.useRef(null);

  /**
   * Handles close events by closing the dialog, turning off the loading state, and clearing the data references
   */
  function handleClose () {
    setOpen(false);
    setIsLoading(false);

    optionsRef.current = null;
    titleRef.current = null;
    messageRef.current = null;
    onConfirmRef.current = null;
    onCancelRef.current = null;
  }

  /**
   * Handles cancel button clicks on the confirmation dialog by calling the cancel callback (if defined) and closing the dialog
   */
  function handleCancel () {
    onCancelRef.current?.();
    handleClose();
  }

  /**
   * Handles confirm button clicks on the confirmation dialog by calling the confirm callback
   */
  async function handleConfirm () {
    setIsLoading(true);

    // Using a try/catch with an await handles both scenarios where the onConfirm callback may or may not return a promise
    // Rejected promises cause the await to throw an exception, which is handled in the catch function
    try {
      await onConfirmRef.current();
      handleClose();
    } catch (_) {
      setIsLoading(false);
    }
  }

  /**
   * Handles calls to the confirm dialog api function by saving the dialog settings and opening the dialog component
   * @param {string} title - title of the dialog
   * @param {string} message - message of the dialog
   * @param {() => void} onConfirm - callback if the user clicks the confirm button on the dialog
   * @param {() => void} onCancel - (optional) callback if the user clicks the cancel button on the dialog
   */
  function handleConfirmDialog (title, message, onConfirm, onCancel = null, options = {}) {
    optionsRef.current = options;
    titleRef.current = title;
    messageRef.current = message;
    onConfirmRef.current = onConfirm;
    onCancelRef.current = onCancel;

    setOpen(true);
  }

  // Confirm dialog function must be memoized to prevent unnecessary re-renders from components consuming this context
  const onConfirmDialog = React.useCallback(handleConfirmDialog, []);

  return (
    <ConfirmDialogContext.Provider value={onConfirmDialog}>
      {children}
      <ConfirmDialog
        isLoading={isLoading}
        message={messageRef.current}
        onCancel={() => handleCancel()}
        onConfirm={() => handleConfirm()}
        open={open}
        options={optionsRef.current}
        title={titleRef.current}
      />
    </ConfirmDialogContext.Provider>
  );
};

ConfirmDialogProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired
};
